import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { CellReferenceSchema, type CellReference } from '../schemas'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** Resolve one saved citation without fetching the table’s other rows. */
export async function getTableReference(tableId: string, referenceId: string, signal?: AbortSignal): Promise<CellReference> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${tableId}/references/${referenceId}`
  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal })
  const { data, parseError } = await readResponseJson(response)
  signal?.throwIfAborted()
  if (!response.ok) {
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    if (response.status >= 500) Sentry.captureException(new Error('Table reference request failed'), { extra: getResponseDebugContext(response, url) })
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' ? detail : 'Unable to open this saved reference.',
      response.status < 500 ? detail : undefined
    )
  }
  const parsed = CellReferenceSchema.safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid table reference response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The saved reference could not be read. Please retry.')
  }
  return parsed.data
}
